import { useCallback, useMemo } from 'react';
import { skipToken, useQueries, UseQueryResult } from '@tanstack/react-query';
import {
  CaseCoverageType,
  QueryResultType,
  StatisticsItemType,
  UseMultiSuggestStatisticsProps,
} from '@lp/features/MultiSuggest';
import { generateCoverageItems } from '@lp/features/MultiSuggest/utils';
import { LogisticsProjectionType } from '@/api/apis/route';
import { Case } from '@/api/apis/case';
import { caseQueryKeys } from '@/queries/case';
import { multiSuggestKeys } from '@/queries/multi-suggest';
import { routePlannerQueryKeys } from '@/queries/route-planner/route-planner.keys';
import { MultiSuggestRoutePlanType } from '@/utils/route/chains';

type UseMultiSuggestCoverageReturnType = {
  coverageItems: StatisticsItemType[];
  isLoading: boolean;
};

type UseMultiSuggestCoverageProps = UseMultiSuggestStatisticsProps & {
  isOverallStatistics: boolean;
};

export const useMultiSuggestCoverage = ({
  taskId,
  caseIds,
  logisticsIds,
  chainNumber,
  isOverallStatistics,
}: UseMultiSuggestCoverageProps): UseMultiSuggestCoverageReturnType => {
  const caseList = useQueries<Case[], QueryResultType<Case>[]>({
    queries: caseIds.map((id) => ({
      queryKey: caseQueryKeys.getItem(id),
      queryFn: skipToken,
    })),
    combine: useCallback(
      (result: UseQueryResult[]) =>
        result.map((r) => ({ data: r.data, isLoading: r.isPending })) as QueryResultType<Case>[],
      [],
    ),
  });

  const logisticsList = useQueries<LogisticsProjectionType[], QueryResultType<LogisticsProjectionType>[]>({
    queries: logisticsIds.map((id) => ({
      queryKey: routePlannerQueryKeys.getLogistics(id),
      queryFn: skipToken,
    })),
    combine: useCallback(
      (result: UseQueryResult[]) =>
        result.map((r) => ({ data: r.data, isLoading: r.isPending })) as QueryResultType<LogisticsProjectionType>[],
      [],
    ),
  });

  const suggestedList = useQueries<
    (MultiSuggestRoutePlanType | null)[],
    QueryResultType<MultiSuggestRoutePlanType | null>[]
  >({
    queries: caseIds.map((id) => ({
      queryKey: multiSuggestKeys.getMultiSuggestCaseResultByCase(taskId, chainNumber, id),
      queryFn: skipToken,
    })),
    combine: useCallback(
      (result: UseQueryResult[]) =>
        result.map((r) => ({
          data: r.data,
          isLoading: r.isPending,
        })) as QueryResultType<MultiSuggestRoutePlanType | null>[],
      [],
    ),
  });

  const isLoading = useMemo(() => {
    const isCasesLoading = caseList.some((r) => r.isLoading);
    const isLogisticsLoading = logisticsList.some((r) => r.isLoading);
    const isSuggestedLoading = suggestedList.some((r) => r.isLoading);

    return isCasesLoading || isLogisticsLoading || isSuggestedLoading;
  }, [caseList, logisticsList, suggestedList]);

  const combinedCaseLogisticsData = useMemo<CaseCoverageType[]>(() => {
    if (isLoading) return [];

    return caseList.reduce((acc, item) => {
      const logistics = logisticsList.find((l) => l.data.caseId === item.data.caseId);
      const plan = suggestedList.find((l) => l.data?.caseId === item.data.caseId);

      const hasLogistics = isOverallStatistics ? logistics?.data?.routes.some((r) => r.legs.length) : logistics?.data;

      if (!plan?.data || !hasLogistics) return acc;

      const data = {
        caseId: item.data.caseId,
        serviceType: item.data.serviceType,
        logistics: {
          routes: logistics!.data.routes,
          resources: logistics!.data.resources,
        },
        plan: {
          legs: plan.data.legs,
          resources: plan.data.resources,
          hasRealCommercialFlight: plan.data.hasRealCommercialFlight,
          hasFakeCommercialFlights: plan.data.hasFakeCommercialFlights,
          ocsWarning: plan.data.ocsWarning,
          specialistWarning: plan.data.specialistWarning,
        },
      };

      return [...acc, data];
    }, [] as CaseCoverageType[]);
  }, [caseList, logisticsList, suggestedList, isLoading, isOverallStatistics]);

  const coverageItems = useMemo(() => {
    if (!combinedCaseLogisticsData.length) return [];
    return generateCoverageItems(combinedCaseLogisticsData);
  }, [combinedCaseLogisticsData]);

  return {
    isLoading,
    coverageItems,
  };
};
